import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Paper,
  CircularProgress,
  Select,
  FormControl,
  MenuItem,
} from "@mui/material";
import { ChevronLeft, ChevronRight, CalendarToday } from "@mui/icons-material";
import { APPOINTMENT_MANAGEMENT_GOOGLE_CALENDAR_MONTH_VIEW } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { ColorsConstants } from "../../cssUtils/constants";
import { formatWithTzLabel } from "../../utils/dateUtils";

const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const eventColors = ["#E8F5E9", "#FFF9C4", "#E3F2FD", "#FCE4EC"];
const botBridgeEventColor = "#ff7300";

const CalendarView = () => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");
  const role = localStorage.getItem("role");

  const isAdmin = role === "admin";

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const fetchEvents = async () => {
    setLoadingEvents(true);

    try {
      const token = await getValidToken();

      const params = {
        role,
        year,
        month: month + 1,
      };

      if (selectedAgent) {
        params.agent_email = selectedAgent.email;
      }

      const queryParams = new URLSearchParams(params);

      const res = await fetch(
        `${APPOINTMENT_MANAGEMENT_GOOGLE_CALENDAR_MONTH_VIEW}?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        },
      );

      const data = await res.json();

      if (data.disabled) {
        setDisabled(true);
      }

      setEvents(data.events || []);
      setAgents(data.agents || []);
    } catch (err) {
      console.error("Error Fetching Events: ", err);
    } finally {
      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [year, month, selectedAgent]);

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDay = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const eventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return events.filter((e) => e.start.startsWith(dateStr));
  };

  if (disabled) {
    return (
      <Box textAlign="center">
        <Typography variant="h6" mt={6} fontWeight="bold" color="red">
          Google Calendar Integration Disabled
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Agents */}
      {isAdmin && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Agent:</Typography>

          <FormControl size="small">
            <Select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              displayEmpty
              renderValue={(selected) =>
                selected ? selected.name || "N/A" : "Select Agent"
              }
              sx={{
                borderRadius: 1,
              }}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Typography>{agent.name || "N/A"}</Typography>
                    <Typography
                      variant="caption"
                      color={ColorsConstants.primaryGray40}
                    >
                      {agent.email || "N/A"}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {loadingEvents ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={400}
        >
          <CircularProgress size={40} />
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday color="primary" />
              <Typography fontSize={20} fontWeight={600}>
                {firstDay.toLocaleString("default", { month: "long" })} {year}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    backgroundColor: botBridgeEventColor,
                    borderRadius: 0.5,
                  }}
                />
                <Typography>= Botbridge Appointments</Typography>
              </Box>
              <IconButton onClick={prevMonth}>
                <ChevronLeft />
              </IconButton>
              <IconButton onClick={nextMonth}>
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          {/* Days Header */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(7, 1fr)"
            borderBottom="1px solid #E0E0E0"
          >
            {days.map((day) => (
              <Typography
                key={day}
                align="center"
                fontWeight={600}
                color="text.secondary"
                py={1}
              >
                {day}
              </Typography>
            ))}
          </Box>

          {/* Calendar Grid */}
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)">
            {Array.from({ length: startDay }).map((_, i) => (
              <Box key={`empty-${i}`} height={120} border="1px solid #E0E0E0" />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayEvents = eventsForDay(day);
              const isToday =
                day === today.getDate() &&
                month === today.getMonth() &&
                year === today.getFullYear();

              return (
                <Box
                  key={day}
                  height={120}
                  border="1px solid #E0E0E0"
                  p={1}
                  bgcolor={isToday ? "#F3F8FF" : "#FFF"}
                >
                  <Typography fontWeight={600}>{day}</Typography>

                  {dayEvents.map((event, idx) => (
                    <Box
                      key={event.id}
                      mt={0.5}
                      px={1}
                      py={0.5}
                      borderRadius={1}
                      fontSize={12}
                      sx={{
                        backgroundColor: event.botBridgeEvent
                          ? botBridgeEventColor
                          : eventColors[idx % eventColors.length],
                        color: event.botBridgeEvent ? "white" : "black",
                        fontWeight: event.botBridgeEvent ? "bold" : "normal",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      onClick={() => setSelectedEvent(event)}
                    >
                      {event.allDay
                        ? event.title
                        : `${formatWithTzLabel(event.start, "hh:mm A")} - ${event.title}`}
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>

          {/* Event Details Modal */}
          <Modal open={!!selectedEvent} onClose={() => setSelectedEvent(null)}>
            <Paper
              sx={{
                width: 420,
                p: 3,
                mx: "auto",
                mt: "15%",
                borderRadius: 2,
              }}
            >
              {selectedEvent && (
                <>
                  <Typography fontWeight={700} mb={0.5}>
                    {selectedEvent.title}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {selectedEvent.calendarName}
                  </Typography>

                  {selectedEvent.botBridgeEvent && (
                    <Typography variant="body2" mb={0.5}>
                      <strong>Appointment ID: </strong> APT-
                      {selectedEvent.appointmentId}
                    </Typography>
                  )}

                  <Typography variant="body2" mb={0.5}>
                    <strong>Start:</strong>{" "}
                    {formatWithTzLabel(selectedEvent.start)}
                  </Typography>

                  <Typography variant="body2" mb={2}>
                    <strong>End:</strong>{" "}
                    {formatWithTzLabel(selectedEvent.end)}
                  </Typography>

                  {/* Meeting Link */}
                  {selectedEvent.meetingLink && (
                    <Box mb={2}>
                      <Typography variant="body2" fontWeight={600}>
                        Meeting Link
                      </Typography>
                      <a
                        href={selectedEvent.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1976d2",
                          wordBreak: "break-all",
                          fontSize: 14,
                        }}
                      >
                        {selectedEvent.meetingLink}
                      </a>
                    </Box>
                  )}

                  {/* Attendees */}
                  {selectedEvent.attendees?.length > 0 && (
                    <Box>
                      <Typography variant="body2" fontWeight={600} mb={0.5}>
                        Attendees
                      </Typography>

                      {selectedEvent.attendees.map((att, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          color="text.secondary"
                        >
                          • {att.email} ({att.responseStatus})
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Modal>
        </>
      )}
    </Box>
  );
};

export default CalendarView;
